import dayjs from "dayjs";
import { RiDeleteBin6Line } from "react-icons/ri";

import { RESERVATION_STATE } from "@/constants/common";
import type {
  ReservationByState,
  ReservationStateCode,
} from "@/types/reservation";
import type { CellInfo } from "@/types/table";
import { UserByState } from "@/types/user";
import {
  convertTimeRangeToHHMM,
  formatLocationWithFloor,
} from "@/utils/reservation";

export const getTableHeader = (headerList: string[], widths?: string[]) => {
  return headerList.map((item: string, index: number) => {
    return {
      type: "header",
      name: item,
      width: widths?.[index],
    };
  });
};

export const getTableBody = (
  titleList: string[],
  contentList: CellInfo[][],
) => {
  return contentList.map(items => {
    return items.map((item, index) => {
      return {
        ...item,
        name: titleList[index],
        type: "body",
      };
    });
  });
};

// 예약 목록 가져오기
export const getReservationBodyData = (
  reservation: ReservationByState,
  stateCode: ReservationStateCode,
  detailsMethod?: () => void,
  approveMethod?: () => void,
  rejectMethod?: () => void,
) => {
  const {
    rsvtDt,
    startTime,
    endTime,
    plcCd,
    plcNm,
    userName,
    sttCd,
    useCnts,
    cmtNm,
  } = reservation;

  const getActionsColumn = (stateCode: ReservationStateCode) => {
    switch (stateCode) {
      case "approve":
        return {
          actions: [
            { data: "상세", method: detailsMethod },
            {
              data: <RiDeleteBin6Line size={16} className="text-red" />,
              method: rejectMethod,
            },
          ],
        };
      case "reject":
        return {
          actions: [
            { data: "상세", method: detailsMethod },
            {
              data: <RiDeleteBin6Line size={16} className="text-red" />,
              method: rejectMethod,
            },
          ],
        };
      case "cancel":
        return {
          actions: [
            { data: "상세", method: detailsMethod },
            {
              data: <RiDeleteBin6Line size={16} className="text-red" />,
              method: rejectMethod,
            },
          ],
        };
      case "request":
        return {
          actions: [
            { data: "상세", method: detailsMethod },
            { data: "승인", method: approveMethod },
            { data: "거절", method: rejectMethod },
          ],
        };
      case "deleted":
        return {
          actions: [{ data: "상세", method: detailsMethod }],
        };
      default:
        return { actions: [] };
    }
  };

  return [
    { data: dayjs(rsvtDt).locale("ko").format("MM/DD (ddd)") },
    { data: convertTimeRangeToHHMM(startTime, endTime) },
    { data: formatLocationWithFloor(plcCd, plcNm) },
    { data: cmtNm },
    { data: userName },
    { data: RESERVATION_STATE[sttCd] },
    { data: useCnts },
    getActionsColumn(stateCode),
  ];
};

// 나의 예약 가져오기
export const getMyReservationBodyData = (
  reservation: ReservationByState,
  stateCode: ReservationStateCode,
  rejectMethod?: () => void,
) => {
  const { rsvtDt, startTime, endTime, plcCd, plcNm, sttCd, useCnts } =
    reservation;

  const getMethodColumn = (stateCode: ReservationStateCode) => {

    const isCancellable = !dayjs(rsvtDt).isBefore(dayjs(), "day"); // ✅ 당일 포함 미래 예약이면 취소 가능

    switch (stateCode) {
      case "approve":
          return isCancellable
          ? [
            {
              data: "승인취소",
              method: rejectMethod,
            },
          ]
        : [{}];
      case "reject":
        return [{}];
      case "request":
        return [
          {
            data: "취소",
            method: rejectMethod,
          },
        ];
      default:
        return [{}];
    }
  };

  return [
    { data: dayjs(rsvtDt).locale("ko").format("YYYY / MM / DD (ddd)") },
    { data: convertTimeRangeToHHMM(startTime, endTime) },
    { data: formatLocationWithFloor(plcCd, plcNm) },
    { data: useCnts },
    { data: RESERVATION_STATE[sttCd] },
    ...getMethodColumn(stateCode),
  ];
};

export const getUserBodyData = (
  user: UserByState,
  detailsMethod?: () => void,
  approveMethod?: () => void,
  rejectMethod?: () => void,
  updateLeaderMethod?: () => void,
) => {
  const {
    createdDate,
    userName,
    // telNo,
    cmtNm,
    // garNm,
    // leafNm,
    roleId,
    userStateCodeName,
    userStateCode,
  } = user;

  const getMethodColumn = (stateCode: string) => {
    switch (stateCode) {
      // 대기
      case "00":
        return [
          { data: "승인", method: approveMethod },
          { data: "거절", method: rejectMethod },
          roleId === "USER"
            ? {
                data: "권한 부여",
                method: updateLeaderMethod,
              }
            : {},
        ];

      // 정상 (승인 완료)
      case "01":
        const roleList = ["USER", "MINISTRY", "LEADER", "ADMIN"];
        return [
          { data: "상세", method: detailsMethod },
          {},
            roleList.includes(roleId)
            ? {
                data: "권한 부여",
                method: updateLeaderMethod,
              }
            : {},
        ];
      default:
        return [{ data: "" }, { data: "" }, { data: "" }];
    }
  };

  return [
    { data: userName },
    { data: cmtNm },
    // { data: garNm },
    // { data: leafNm },
    // { data: telNo }, // 상세에서 볼 수 있으므로 주석 처리
    { data: roleId },
    { data: userStateCodeName },
    { data: dayjs(createdDate).locale("ko").format("YYYY / MM / DD (ddd)") },
    ...getMethodColumn(userStateCode),
  ];
};
